import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X } from "lucide-react";
import { MessagePartInputExtended } from "@/pages/BulkMessaging/types";
import { WYSIWYGEditor } from "../WYSIWYGEditor";
import { Button } from "../ui/Button";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/Avatar";
import { InlineImageUpload } from "./InlineImageUpload";
import { DragHandle } from "./DragHandle";
import { getMessagePartContent } from "@/pages/BulkMessaging/utils/apiHelpers";
import { useOrg } from "@/hooks/useOrg";
import { Logo } from "@/assets/svg/Logo";

interface MessageBubbleProps {
  messagePart: MessagePartInputExtended;
  index: number;
  onEdit: (index: number, content?: string, images?: File[]) => void;
  onDelete: (index: number) => void;
  senderName?: string;
  senderProfilePhoto?: { url?: string | null } | null;
  showControls?: boolean;
}

export const MessageBubble = ({
  messagePart,
  index,
  onEdit,
  onDelete,
  senderName,
  senderProfilePhoto,
  showControls = true,
}: MessageBubbleProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.composeMessage",
  });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    overflow: "hidden",
    maxWidth: "100%",
  };

  const { org } = useOrg();

  const renderContent = useCallback(() => {
    const content = getMessagePartContent(messagePart);

    if (content.text !== undefined && !content.isImageElement) {
      return (
        <WYSIWYGEditor
          content={content.text || ""}
          onContentChange={(content: string) => onEdit(index, content)}
        />
      );
    }

    if (content.attachment || content.isImageElement) {
      const images = content.attachment ? [content.attachment] : [];
      return (
        <InlineImageUpload
          images={images}
          onChange={(images: File[]) => onEdit(index, undefined, images)}
        />
      );
    }

    return null;
  }, [messagePart, onEdit, index]);

  const avatarSrc = senderProfilePhoto?.url || org?.logo?.url;
  const avatarFallback = senderName?.charAt(0) || "U";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-start gap-1.5"
    >
      <div className="flex-shrink-0">
        <Avatar>
          {avatarSrc ? (
            <>
              <AvatarImage src={avatarSrc} alt={t("senderAvatarAlt")} />
              <AvatarFallback>{avatarFallback}</AvatarFallback>
            </>
          ) : (
            <div data-testid="logo-fallback">
              <Logo />
            </div>
          )}
        </Avatar>
      </div>

      <div className="w-80 rounded-lg">
        <div>
          <span className="text-sm font-medium text-foreground">
            {senderName || t("defaultSenderName")}
          </span>
        </div>

        <div className="relative mt-2">
          {showControls && (
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index);
              }}
              className="absolute -top-2 -right-2 z-20 h-6 w-6 rounded-full bg-foreground p-0 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 hover:bg-muted-foreground"
              aria-label={t("deleteMessage")}
            >
              <X className="h-4 w-4 text-background" />
            </Button>
          )}

          {renderContent()}
        </div>
      </div>

      {showControls && (
        <DragHandle attributes={attributes} listeners={listeners} />
      )}
    </div>
  );
};
